import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance } from '../App';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react';

const DataDeletion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [summary, setSummary] = useState(null);

  const handleCancel = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/settings');
    }
  };

  const confirmed = typed.trim().toUpperCase() === 'DELETE';

  const handleDelete = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axiosInstance.delete('/user/account', { data: { reason } });
      setSummary(res.data?.summary || null);
      setSubmitted(true);
      try {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
      } catch (e) { /* noop */ }
      if (typeof logout === 'function') {
        try { logout(); } catch (e) { /* noop */ }
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('dataDeletion.errDefault', 'Could not delete account. Please try again or contact support.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center" data-testid="deletion-submitted">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('dataDeletion.doneTitle', 'Account deleted')}</h2>
          <p className="text-gray-600 mb-4">
            {t('dataDeletion.doneBody', 'Your account has been permanently removed. Public reviews have been anonymised. You can sign up again any time with a fresh account.')}
          </p>
          {summary?.stripe_subscription_cancelled && (
            <p className="text-sm text-gray-500 mb-4">{t('dataDeletion.doneSubCancelled', 'Your active subscription was cancelled.')}</p>
          )}
          {summary?.businesses_unclaimed > 0 && (
            <p className="text-sm text-gray-500 mb-4">
              {t('dataDeletion.doneUnclaimed', 'Your business listing has been returned to an unclaimed state so the community can still find it.')}
            </p>
          )}
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            data-testid="deletion-home-btn"
          >
            {t('dataDeletion.backHome', 'Back to home')}
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('dataDeletion.signInRequired', 'Sign in required')}</h2>
          <p className="text-gray-600 mb-4">{t('dataDeletion.signInBody', 'You need to be signed in to delete your account.')}</p>
          <button
            onClick={() => navigate('/signin')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {t('dataDeletion.goSignIn', 'Go to sign in')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-deletion-page">
      <div className="max-w-lg mx-auto px-4 py-8">
        <button
          onClick={handleCancel}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5 mr-2 rtl-flip" /> {t('common.back', 'Back')}
        </button>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{t('dataDeletion.title', 'Delete your account')}</h1>
              <p className="text-sm text-gray-500">{t('dataDeletion.subtitle', 'This action is permanent and cannot be undone.')}</p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex gap-2 text-red-800 font-medium text-sm mb-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {t('dataDeletion.deletedHeading', 'Permanently deleted')}
              </div>
              <ul className="text-sm text-red-700 space-y-1 ml-6 list-disc">
                <li>{t('dataDeletion.deletedItem1', 'Your account, email and password')}</li>
                <li>{t('dataDeletion.deletedItem2', 'Saved favourites and notification inbox')}</li>
                <li>{t('dataDeletion.deletedItem3', 'Notification subscriptions and device links')}</li>
                <li>{t('dataDeletion.deletedItem4', 'Any pending reports or claim requests you made')}</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 font-medium text-sm mb-2">{t('dataDeletion.anonHeading', 'Anonymised (kept publicly, your name removed)')}</p>
              <ul className="text-sm text-amber-700 space-y-1 ml-4 list-disc">
                <li>{t('dataDeletion.anonItem1', 'Reviews you\'ve written — shown as "Deleted user" on the business page')}</li>
                <li>{t('dataDeletion.anonItem2', 'Moderation/audit records that have already been actioned')}</li>
              </ul>
            </div>

            {user.role === 'business' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 font-medium text-sm mb-2">{t('dataDeletion.bizHeading', 'If you own a business listing')}</p>
                <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
                  <li>{t('dataDeletion.bizItem1', 'Your subscription will be cancelled immediately via Stripe')}</li>
                  <li>{t('dataDeletion.bizItem2', 'The listing will be returned to an unclaimed state (so locals can still find it)')}</li>
                  <li>{t('dataDeletion.bizItem3', 'You can claim it again later with a new account if you come back')}</li>
                </ul>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-700 font-medium text-sm mb-2">{t('dataDeletion.legalHeading', 'Retained for legal/tax reasons')}</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>{t('dataDeletion.legalItem1', 'Stripe invoices (held by Stripe — Spanish tax law requires 6 years retention)')}</li>
                <li>{t('dataDeletion.legalItem2', 'Anonymised deletion audit record (kept 2 years to prove compliance)')}</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dataDeletion.reasonLabel', 'Reason for leaving')} <span className="text-gray-400 font-normal">({t('common.optional', 'optional')})</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('dataDeletion.reasonPlaceholder', 'Help us improve — what prompted you to leave?')}
                data-testid="deletion-reason"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dataDeletion.typeDeleteLead', 'Type')} <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">DELETE</span> {t('dataDeletion.typeDeleteTrail', 'to confirm')}
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="DELETE"
                autoComplete="off"
                data-testid="deletion-confirm-input"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}

            <button
              onClick={handleDelete}
              disabled={!confirmed || submitting}
              className={`w-full py-3 font-semibold rounded-lg transition-colors ${
                confirmed && !submitting
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="confirm-delete-account-btn"
            >
              {submitting ? t('dataDeletion.deleting', 'Deleting…') : t('dataDeletion.deletePermanent', 'Delete my account permanently')}
            </button>

            <button
              onClick={handleCancel}
              className="w-full py-2.5 text-sm text-gray-600 hover:text-gray-800"
              data-testid="cancel-deletion-btn"
            >
              {t('dataDeletion.cancelKeep', 'Cancel, keep my account')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
